import { t } from 'i18next';
import { FC, useCallback, useEffect, useState } from 'react';

import { DefaultCustomRatePayload } from '../../../store/_declarations/financialCalculator';
import {
  MasterAgreement,
  Parameters
} from '../../../store/_declarations/target';
import { montlyRateCalculationApi } from '../../../store/api/financialCalculator';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { createArraysDownpayment, reduceFunc } from '../../../utils/array';
import { formatNumber } from '../../../utils/formatters';
import {
  decimalsSeparator,
  isVatInluded,
  thousandsSeparator
} from '../../../utils/marketSpecs';
import { getAmountWithVat } from '../../../utils/taxes';
import Font from '../../atoms/Font';
import SliderInput from '../../atoms/SliderInput';
import { ControlsWrapper, WrapperControlsTax } from './styles';

const getNewGlobalMileageForNewDuration = (
  previousGlobalMileage?: number,
  actualDuration?: number,
  newDuration?: number
) => {
  try {
    if (!previousGlobalMileage || !actualDuration || !newDuration) {
      throw new Error('Invalid parameters');
    }
    const montlyMileage = previousGlobalMileage / actualDuration;
    return montlyMileage * newDuration;
  } catch {
    return 0;
  }
};

const getAnnualMileage = (
  totalKmContract: number,
  contractDuration: number
) => {
  try {
    return Math.round((totalKmContract / contractDuration) * 12);
  } catch {
    return 0;
  }
};

const getNewGlobalMileage = (duration?: number, annualMileage?: number) => {
  try {
    if (!duration || !annualMileage) {
      throw new Error('Invalid parameters');
    }

    return (annualMileage / 12) * duration;
  } catch {
    return 0;
  }
};

type ControllersProps = {
  item: MasterAgreement;
};
const Controllers: FC<ControllersProps> = ({ item }) => {
  const { consumerPayload, widgetType } = useAppSelector((state) => state.app);
  const {
    listByVehicle,
    montlyRateCalculation,
    montlyRateCalculationPayloads,
    quotationLoading
  } = useAppSelector((state) => state.financialCalculator);

  const dispatch = useAppDispatch();
  const withVat = isVatInluded(
    consumerPayload?.vehicle,
    consumerPayload?.context.customer,
    consumerPayload?.context.countryCode
  );

  const [currentParameters, setCurrentParameters] =
    useState<DefaultCustomRatePayload.Parameters>();

  const { downpaymentsWithVat, downpaymentsWithoutVat } =
    createArraysDownpayment({
      b2c: withVat,
      deposit:
        ((consumerPayload?.vehicle?.pricing?.basicPriceExclTax || 0) * 50) /
          100 || 0,
      depositWithVat:
        (getAmountWithVat(
          consumerPayload?.vehicle?.pricing?.basicPriceExclTax || 0
        ) *
          50) /
          100 || 0
    });

  useEffect(() => {
    if (
      item &&
      !montlyRateCalculationPayloads?.[item.masterAgreement] &&
      montlyRateCalculation?.[item.masterAgreement] !== 'rejected' &&
      !quotationLoading
    ) {
      const month = item.options.month.find((m) => m.isDefault)?.month || 0;
      const km = item.options.km.find((okm) => okm.isDefault)?.km || 0;
      const globalMileage = (km / 12) * month;
      const cashDeposit = downpaymentsWithoutVat.reduce(
        reduceFunc(item.downPaymentAmount)
      );
      dispatch(
        montlyRateCalculationApi({
          masterAgreement: item.masterAgreement,
          pricingProcedure: item.pricingProcedure,
          widgetSelection: {
            parameters: {
              cashDeposit,
              globalMileage,
              km,
              masterAgreement: item.masterAgreement,
              mileAllowKm: Number(item.mileAllowKm),
              mileAllowKmPerc: Number(item.mileAllowKmPerc),
              month,
              services: item.services.filter(
                (s) =>
                  s.isMandatory ||
                  s.isMandatoryCategory ||
                  s.zzRecomCategory ||
                  !s.isVisible
              )
            },
            vehicle: {
              accessory: item.accessory
            }
          }
        })
      );
    }
  }, [item, quotationLoading]);

  useEffect(() => {
    const arrayValues = withVat ? downpaymentsWithVat : downpaymentsWithoutVat;
    const parseToIntOrUndefined = (value: any): number | undefined =>
      value ? parseInt(value, 10) : undefined;
    const getCashDeposit = (paramCashDeposit?: number): number | undefined => {
      if (!paramCashDeposit) {
        return 0;
      }

      const countryCode = consumerPayload?.context.countryCode?.toUpperCase();

      if (countryCode === 'UK') {
        return undefined;
      }

      const closestValue = downpaymentsWithoutVat.reduce(
        reduceFunc(paramCashDeposit)
      );

      const cashDeposit = paramCashDeposit
        ? arrayValues[downpaymentsWithoutVat.indexOf(closestValue)]
        : 0;
      return parseToIntOrUndefined(cashDeposit);
    };

    if (montlyRateCalculationPayloads?.[item.masterAgreement]) {
      const {
        widgetSelection: { parameters }
      } = montlyRateCalculationPayloads[item.masterAgreement];
      if (parameters) {
        const p = {
          annualMileage: parameters.km,
          cashDeposit: getCashDeposit(parameters.cashDeposit),
          duration: parameters.month,
          globalMileage: parameters.globalMileage,
          numberOfInitialRentals: 0 // TODO: to fix for uk market
        };
        setCurrentParameters(p);
      }
    } else {
      const deposit = item.downPaymentPercentage
        ? ((consumerPayload?.vehicle.pricing?.basicPriceExclTax || 0) *
            item.downPaymentPercentage) /
          100
        : item.downPaymentAmount;

      const p = {
        annualMileage: item.options.km.find((o) => o.isDefault)?.km,
        cashDeposit: getCashDeposit(deposit),
        duration: item.options.month.find((o) => o.isDefault)?.month,
        globalMileage: getNewGlobalMileage(
          item.options.month.find((o) => o.isDefault)?.month,
          item.options.km.find((o) => o.isDefault)?.km
        ),
        numberOfInitialRentals: 0 // TODO: to fix for uk market
      };
      setCurrentParameters(p);
    }
  }, [montlyRateCalculationPayloads]);

  const formatterAmount = useCallback(
    (value: number) =>
      formatNumber(value, {
        decimalsSeparator: decimalsSeparator(
          consumerPayload?.context.countryCode
        ),
        hideDecimals: true,
        tousandSeparator: thousandsSeparator(
          consumerPayload?.context.countryCode
        )
      }),
    [consumerPayload?.context.countryCode]
  );

  const handleUpdateParams = (
    name: keyof DefaultCustomRatePayload.Parameters,
    value: number
  ) => {
    let newValues = { [name]: value };
    if (currentParameters && name === 'globalMileage') {
      newValues = {
        annualMileage: value,
        globalMileage: getNewGlobalMileage(currentParameters.duration, value)
      };
    }
    if (currentParameters && name === 'duration') {
      newValues = {
        duration: value,
        globalMileage: getNewGlobalMileageForNewDuration(
          currentParameters.globalMileage,
          currentParameters.duration,
          value
        )
      };
    }

    const p = {
      ...(consumerPayload?.parameters || {}),
      ...currentParameters,
      ...newValues
    };
    setCurrentParameters(p);
  };
  const handleUpdateRate = (
    parameters: DefaultCustomRatePayload.Parameters
  ) => {
    if (listByVehicle) {
      const month = parameters.duration || 0;
      const km = parameters.annualMileage || 0;
      const globalMileage = parameters.globalMileage || 0;

      const parametersPayload: Parameters = {
        cashDeposit: parameters.cashDeposit,
        globalMileage,
        km,
        masterAgreement: item.masterAgreement,
        mileAllowKm: Number(item.mileAllowKm),
        mileAllowKmPerc: Number(item.mileAllowKmPerc),
        month
      };

      dispatch(
        montlyRateCalculationApi({
          masterAgreement: item.masterAgreement,
          pricingProcedure: item.pricingProcedure,
          widgetSelection: {
            parameters: {
              ...parametersPayload
            },
            vehicle: {
              accessory: item.accessory
            }
          }
        })
      );
    }
  };
  const handleSliderChange = (
    name: keyof DefaultCustomRatePayload.Parameters,
    value: number
  ) => {
    if (consumerPayload) {
      const arrayValues = withVat
        ? downpaymentsWithVat
        : downpaymentsWithoutVat;

      const cashDeposit =
        downpaymentsWithoutVat[
          arrayValues.indexOf(currentParameters?.cashDeposit || 0)
        ];

      const parameters: any = {
        ...currentParameters,
        cashDeposit
      };

      switch (name) {
        case 'duration':
          parameters.globalMileage =
            (parameters.globalMileage / (parameters.duration / 12)) *
            (value / 12);
          parameters.duration = value;
          break;
        case 'cashDeposit':
          parameters.cashDeposit =
            downpaymentsWithoutVat[
              withVat
                ? downpaymentsWithVat.indexOf(value)
                : downpaymentsWithoutVat.indexOf(value)
            ];
          break;
        case 'globalMileage':
          parameters.globalMileage = (parameters.duration / 12) * value;
          parameters.annualMileage = value;
          break;
        default:
          parameters[name] = value;
          break;
      }
      handleUpdateRate({
        ...consumerPayload.parameters,
        ...parameters
      });

      handleUpdateParams(name, value);
    }
  };

  return (
    <WrapperControlsTax>
      <ControlsWrapper $widgetType={widgetType} data-testid="controls-wrapper">
        {/* {consumerPayload?.context.countryCode === 'UK' ? (
        <SliderInput
          buttonName={`${t('InitialRentals')}`}
          initialValue={currentParameters?.numberOfInitialRentals}
          isUnitMeasureVisible
          labelName={`${t('InitialRentals')}`}
          onChange={(value) =>
            handleSliderChange('numberOfInitialRentals', value)
          }
          tickmarks={item?.criteriaAdjustment?.deposits || []}
          unitMeasure={`${t('UnitMeasureMonths')}`}
        />
      ) : ( */}
        <SliderInput
          buttonName={t('Downpayment')}
          extraText="*"
          formatter={formatterAmount}
          hideTickmarks
          initialValue={currentParameters?.cashDeposit}
          isUnitMeasureVisible
          labelName={t('Downpayment')}
          onChange={(value) => handleSliderChange('cashDeposit', value)}
          tickmarks={withVat ? downpaymentsWithVat : downpaymentsWithoutVat}
          unitMeasure={t('Currency')}
        />
        <SliderInput
          buttonName={t('Duration')}
          initialValue={currentParameters?.duration}
          isUnitMeasureVisible
          labelName={t('Duration')}
          onChange={(value) => handleSliderChange('duration', value)}
          tickmarks={item.options.month.map((o) => o.month)}
          unitMeasure={t('UnitMeasureMonths')}
        />
        <SliderInput
          buttonName={t('AnnualMilage')}
          formatter={formatterAmount}
          initialValue={getAnnualMileage(
            currentParameters?.globalMileage || 0,
            currentParameters?.duration || 0
          )}
          isUnitMeasureVisible
          labelName={t('AnnualMilage')}
          onChange={(value) => handleSliderChange('globalMileage', value)}
          tickmarks={item.options.km.map((o) => o.km)}
          unitMeasure={t('UnitMeasureKm')}
        />
      </ControlsWrapper>
      {consumerPayload?.context.countryCode?.toUpperCase() !== 'UK' && (
        <Font customConfiguration={{ color: 'neutral400' }} variant="bodySBold">
          *{withVat ? t('VATinc') : t('VATexcl')}
        </Font>
      )}
    </WrapperControlsTax>
  );
};

export default Controllers;
